/**
 * List of all resource types
 */
export const RESOURCE_TYPES = [
    "metal",
    "minerals",
    "gas",
    "energy",
    "ozone",
];
/**
 * Display names for resources, plus scrap (a stockpile kept outside
 * `ResourceType`). Every player-facing mention of a resource reads from here;
 * the keys are internal and never shown.
 */
export const RESOURCE_NAMES = {
    metal: "Carbon",
    minerals: "Crystals",
    gas: "Hydrogen",
    energy: "Energy",
    ozone: "Water",
    scrap: "Salvage",
};
/**
 * Length of a single resource-production tick.
 */
export const TICK_INTERVAL_SECONDS = 60;
/**
 * Starting per-hour production rates for a new planet's economic resources —
 * the planet's own baseline income before any mine building contributes
 * (issue #103).
 */
export const DEFAULT_RESOURCE_RATES = {
    metal: 20,
    minerals: 15,
    gas: 5,
};
/**
 * Base storage capacity per resource, before storage-building bonuses.
 */
export const DEFAULT_STORAGE_CAPACITY = 100000;
export const OZONE_MAX = 15000;
export const OZONE_CRITICAL_LEVEL = 100;
export const OZONE_REDUCTION_PER_LEVEL_PER_DAY = 10;
export const PHYSICS_BONUS_PER_LEVEL = 0.05;
export const PHYSICS_MAX_LEVEL = 10;
/** Mathematics: +5% Economic (resource-zone) production per level, capped at level 10 (+50%). */
export const MATHEMATICS_BONUS_PER_LEVEL = 0.05;
export const MATHEMATICS_MAX_LEVEL = 10;
/**
 * Robots (Engineering Facility + Automation Center, see `docs/game-details/buildings.md`
 * "Production Bots"): Engineering Facility gives 1 robot/level (max 20), Automation Center
 * gives an extra `ROBOT_FACILITY_BONUS_PER_LEVEL` robots/level (max 5
 * levels = 10), for 30 robots at both maxed. Robots split evenly across
 * Metal/Minerals/Gas (see `distributeRobotsEvenly` in
 * `packages/game-engine/src/resources.ts`), each capped at
 * `ROBOT_MAX_PER_RESOURCE`, each unit worth a flat per-resource % bonus
 * (issue #56): 6% metal, 5% minerals, 4% gas — up to +60%/+50%/+40% when a
 * resource's bucket is full.
 */
export const ROBOT_FACILITY_BONUS_PER_LEVEL = 2;
export const ROBOT_MAX_PER_RESOURCE = 10;
export const ROBOT_METAL_BONUS_PER_UNIT = 0.06;
export const ROBOT_MINERAL_BONUS_PER_UNIT = 0.05;
export const ROBOT_GAS_BONUS_PER_UNIT = 0.04;
/** Ecology: +250 Ozone recovered per day per level, capped at level 15 (+3750/day). */
export const ECOLOGY_RECOVERY_PER_LEVEL_PER_DAY = 250;
export const ECOLOGY_MAX_LEVEL = 15;
/** Planet positions per solar system (C3 = 1–24; C3 = 0 is the Sun). */
export const SYSTEM_POSITION_MAX = 24;
/**
 * Verbatim transcription of the "Planet position and Energy gain" table in
 * `docs/game-details/planet.md` (original Nemexia design data). The table
 * happens to be phrased in Solar-Satellite yield — "the base amount of
 * Energy each Satellite supplies" at 100% sun production — hence the name,
 * but it is the game's only per-position energy data, so its SHAPE (three
 * tiers) doubles as the solar proximity curve for ALL position-dependent
 * energy: the engine never uses these numbers as absolute income, only
 * normalized to position 1's 100 (`calculateProximityMultiplierV2` =
 * table[position]/100, so positions 1-8 → 1.0 (100/100) exactly,
 * positions 9-16 → 90/100 = 0.9, and positions 17-24 → 85/100 = 0.85).
 * Index = position 1–24; the leading 0 pads index 0, which is the Sun
 * coordinate (C3 = 0), not a planet slot.
 */
export const POSITION_BASE_SATELLITE_ENERGY = [
    0,
    100, 100, 100, 100, 100, 100, 100, 100,
    90, 90, 90, 90, 90, 90, 90, 90,
    85, 85, 85, 85, 85, 85, 85, 85,
];
/** Reference sun age used when no galaxy-specific value is available (200-day = mid-life star). */
export const DEFAULT_SUN_AGE_DAYS = 90;
/** Stars emit at least this many days of energy even after full depletion. */
export const SUN_AGE_MIN = 20;
/** Maximum initial sun age assigned to a new star system at universe creation. */
export const SUN_AGE_MAX_DAYS = 300;
/** Number of solar systems per galaxy (positions 1-N; index 0 is always vacant). */
export const SYSTEMS_PER_GALAXY = 40;
/**
 * Every valid `Universe.resourceMultiplier` value. Canonical list — the
 * mock universe service and the `createUniverse` operator script both
 * validate against this instead of each keeping their own copy (issue #53
 * review).
 */
export const RESOURCE_MULTIPLIERS = [1, 3, 5, 10, 20, 100, 1000];
/**
 * Asteroid lifecycle (Astroploatation targets — mission 8 in
 * `docs/game-details/flight_management_system.md`). An asteroid enters the
 * galaxy on one of the first ASTEROID_SPAWN_POSITION_MAX positions of
 * ASTEROID_ORIGIN_SYSTEM, drifts outward one position every 45–60 minutes
 * (fixed per asteroid), crosses into the next system when it passes
 * position 24, and is gone for good once it would pass SYSTEMS_PER_GALAXY.
 * Entry is decided once per ASTEROID_SPAWN_SLOT_MINUTES window at
 * ASTEROID_SPAWN_CHANCE — with these values every system averages ~3–4
 * asteroids in transit at any moment.
 */
export const ASTEROID_SPAWN_POSITION_MAX = 3;
export const ASTEROID_STEP_MINUTES_MIN = 45;
export const ASTEROID_STEP_MINUTES_MAX = 60;
export const ASTEROID_SPAWN_SLOT_MINUTES = 120;
export const ASTEROID_SPAWN_CHANCE = 0.35;
/**
 * The single system asteroids enter the galaxy at. Asteroids drift forward
 * and never despawn until they leave the galaxy, so the galaxy behaves like
 * a conveyor: whatever flows in at one end flows through every system at the
 * same rate, and each of them holds the ~3–4 asteroids above. Seeding a
 * stream in EVERY system instead makes system N hold N times that (system 40
 * ends up with ~150 asteroids across 24 positions) — the bug this constant
 * exists to rule out.
 */
export const ASTEROID_ORIGIN_SYSTEM = 1;
/**
 * Number of asteroid art variants (`asteroid1.png`…`asteroidN.png` in the
 * client's galaxy assets); each asteroid keeps one for its whole life.
 */
export const ASTEROID_IMAGE_COUNT = 6;
/**
 * Number of star art variants (`star1.png`…`starN.png` in the client's
 * galaxy assets); a system's star image is a deterministic hash of its
 * (galaxy, system) coordinates (`getSystemStarImageIndex` in
 * `packages/game-engine`), so it never needs to be stored.
 */
export const STAR_IMAGE_COUNT = 8;
/** Fraction of upfront cost returned when a queue action is cancelled. */
export const REFUND_RATES = {
    building: 0.75,
    ship: 0.70,
    research: 0.60,
};
//# sourceMappingURL=resources.js.map